import React, { useState } from 'react';
import { Trash2, Check, X as XIcon, Plus, Minus, Upload, Loader2, Image as ImageIcon, Info } from 'lucide-react';
import { getUploadUrlWithQuota } from '../../services/uploadWithQuota';
import ColorSwatchPicker from './ColorSwatchPicker';

interface ComparisonColumn {
  id: string;
  title: string;
  price: string;
  features: string[];
  highlighted: boolean;
  buttonText: string;
  buttonUrl: string;
  imageUrl?: string;
  highlightColor?: string;
}

interface ComparisonTableBlockData {
  columns: ComparisonColumn[];
}

interface ComparisonTableBlockProps {
  data: ComparisonTableBlockData;
  onChange: (data: ComparisonTableBlockData) => void;
  onDelete: () => void;
  isPreview?: boolean;
}

const DEFAULT_HIGHLIGHT_COLOR = '#f97316';

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return `rgba(249,115,22,${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const ComparisonTableBlock: React.FC<ComparisonTableBlockProps> = ({ data, onChange, onDelete, isPreview }) => {
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [expandedColumnId, setExpandedColumnId] = useState<string | null>(null);

  // Safety check: ensure data.columns exists
  const safeData = {
    columns: data?.columns || []
  };

  const addColumn = () => {
    const newColumn: ComparisonColumn = {
      id: Date.now().toString(),
      title: 'แพ็คเก็จใหม่',
      price: '0',
      features: ['ฟีเจอร์ 1', 'ฟีเจอร์ 2'],
      highlighted: false,
      buttonText: 'เลือกแพ็คเก็จนี้',
      buttonUrl: '#',
      imageUrl: '',
      highlightColor: DEFAULT_HIGHLIGHT_COLOR,
    };
    onChange({ columns: [...safeData.columns, newColumn] });
    setExpandedColumnId(newColumn.id);
  };

  const toggleExpand = (columnId: string) => {
    setExpandedColumnId(expandedColumnId === columnId ? null : columnId);
  };

  const handleImageUpload = async (columnId: string, file: File) => {
    try {
      setUploadingId(columnId);
      setUploadProgress(0);

      const result = await getUploadUrlWithQuota(
        file.name,
        file.type,
        file.size,
        file,
        (prog, msg) => {
          setUploadProgress(prog);
          console.log(msg);
        }
      );

      updateColumn(columnId, 'imageUrl', result.publicUrl);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('อัพโหลดรูปภาพล้มเหลว กรุณาลองใหม่อีกครั้ง');
    } finally {
      setUploadingId(null);
      setUploadProgress(0);
    }
  };

  const updateColumn = (id: string, field: keyof ComparisonColumn, value: any) => {
    const updatedColumns = safeData.columns.map(col =>
      col.id === id ? { ...col, [field]: value } : col
    );
    onChange({ columns: updatedColumns });
  };

  const deleteColumn = (id: string) => {
    onChange({ columns: safeData.columns.filter(col => col.id !== id) });
  };

  const addFeature = (columnId: string) => {
    const updatedColumns = safeData.columns.map(col =>
      col.id === columnId ? { ...col, features: [...col.features, 'ฟีเจอร์ใหม่'] } : col
    );
    onChange({ columns: updatedColumns });
  };

  const updateFeature = (columnId: string, featureIndex: number, value: string) => {
    const updatedColumns = safeData.columns.map(col => {
      if (col.id === columnId) {
        const newFeatures = [...col.features];
        newFeatures[featureIndex] = value;
        return { ...col, features: newFeatures };
      }
      return col;
    });
    onChange({ columns: updatedColumns });
  };

  const deleteFeature = (columnId: string, featureIndex: number) => {
    const updatedColumns = safeData.columns.map(col => {
      if (col.id === columnId) {
        return { ...col, features: col.features.filter((_, i) => i !== featureIndex) };
      }
      return col;
    });
    onChange({ columns: updatedColumns });
  };

  if (isPreview) {
    return (
      <div className="overflow-x-auto py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {safeData.columns.map((column) => {
            const highlightColor = column.highlightColor || DEFAULT_HIGHLIGHT_COLOR;
            return (
            <div
              key={column.id}
              className={`rounded-2xl overflow-hidden transition-all duration-300 ${
                column.highlighted
                  ? 'text-white shadow-2xl transform scale-105'
                  : 'bg-white border-2 border-gray-200 hover:shadow-xl'
              }`}
              style={column.highlighted
                ? { backgroundColor: highlightColor, boxShadow: `0 0 0 4px ${hexToRgba(highlightColor, 0.3)}` }
                : { borderColor: 'transparent' }}
              onMouseEnter={(e) => { if (!column.highlighted) (e.currentTarget as HTMLElement).style.borderColor = highlightColor; }}
              onMouseLeave={(e) => { if (!column.highlighted) (e.currentTarget as HTMLElement).style.borderColor = ''; }}
            >
              {column.highlighted && (
                <div className="text-sm font-bold px-4 py-2 text-center bg-white" style={{ color: highlightColor }}>
                  ⭐ แนะนำ
                </div>
              )}

              {/* Package Image */}
              {column.imageUrl && (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={column.imageUrl}
                    alt={column.title}
                    className="w-full h-full object-cover"
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-t to-transparent"
                    style={{ backgroundImage: `linear-gradient(to top, ${column.highlighted ? hexToRgba(highlightColor, 0.8) : 'rgba(17,24,39,0.6)'}, transparent)` }}
                  />
                </div>
              )}

              <div className="p-6">
                <h3 className={`text-2xl font-bold mb-3 ${column.highlighted ? 'text-white' : 'text-gray-900'}`}>
                  {column.title}
                </h3>

                <div className="text-5xl font-bold mb-6" style={{ color: column.highlighted ? '#fff' : highlightColor }}>
                  {column.price} <span className="text-2xl">฿</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {column.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                        column.highlighted ? 'bg-white/20' : 'bg-green-100'
                      }`}>
                        <Check className={`w-4 h-4 ${
                          column.highlighted ? 'text-white' : 'text-green-600'
                        }`} />
                      </div>
                      <span className={`text-sm ${
                        column.highlighted ? 'text-white/90' : 'text-gray-700'
                      }`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => {
                    if (column.buttonUrl && column.buttonUrl !== '#') {
                      window.open(column.buttonUrl, '_blank');
                    }
                  }}
                  className={`block w-full py-4 rounded-xl font-bold text-center transition-all duration-300 ${
                    column.highlighted ? 'bg-white shadow-lg' : 'text-white shadow-md'
                  } ${(!column.buttonUrl || column.buttonUrl === '#') ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
                  style={{ backgroundColor: column.highlighted ? '#fff' : highlightColor, color: column.highlighted ? highlightColor : '#fff' }}
                  disabled={!column.buttonUrl || column.buttonUrl === '#'}
                >
                  {column.buttonText}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">ตารางเปรียบเทียบ</h3>
          <div className="relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
            <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
              <p className="font-semibold mb-1">เปรียบเทียบแพ็คเก็จ</p>
              <p className="text-gray-300">แสดงตารางเปรียบเทียบแพ็คเก็จหรือสินค้าต่างๆ ให้ลูกค้าเห็นความแตกต่างและเลือกได้ง่าย</p>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      {/* Inline Accordion List */}
      <div className="space-y-4">
        {safeData.columns.map((column, colIndex) => {
          const isExpanded = expandedColumnId === column.id;
          const highlightColor = column.highlightColor || DEFAULT_HIGHLIGHT_COLOR;
          return (
            <div
              key={column.id}
              className={`border-2 rounded-xl overflow-hidden transition-all ${
                column.highlighted
                  ? 'border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              {/* Collapsed Header */}
              <div
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
                onClick={() => toggleExpand(column.id)}
              >
                {/* Thumbnail */}
                <div className="flex-shrink-0">
                  {column.imageUrl ? (
                    <img
                      src={column.imageUrl}
                      alt={column.title}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Summary Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={`font-bold text-lg truncate ${
                      column.highlighted ? 'text-orange-600' : 'text-gray-900'
                    }`}>
                      {column.title || `แพ็คเก็จ #${colIndex + 1}`}
                    </h4>
                    {column.highlighted && (
                      <span className="flex-shrink-0 bg-orange-100 text-orange-600 text-xs font-bold px-2 py-1 rounded-full">
                        ⭐ แนะนำ
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <span className="font-bold text-orange-600 text-xl">{column.price} ฿</span>
                    <span>•</span>
                    <span>{column.features.length} ฟีเจอร์</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteColumn(column.id);
                    }}
                    className="text-red-500 hover:text-white hover:bg-red-500 p-2 rounded-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  <div className={`transform transition-transform ${
                    isExpanded ? 'rotate-180' : ''
                  }`}>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-gray-200 p-6 bg-gradient-to-br from-white to-gray-50/30 space-y-6">
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-orange-500" />
                      รูปภาพแพ็คเก็จ
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleImageUpload(column.id, file);
                          e.target.value = '';
                        }
                      }}
                      disabled={uploadingId === column.id}
                      className="hidden"
                      id={`image-upload-${column.id}`}
                    />
                    {column.imageUrl ? (
                      <div className="relative group">
                        <img
                          src={column.imageUrl}
                          alt={column.title}
                          className="w-full h-48 object-cover rounded-xl"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all rounded-xl flex items-center justify-center gap-3">
                          <label
                            htmlFor={`image-upload-${column.id}`}
                            className="cursor-pointer bg-white text-orange-600 px-4 py-2 rounded-lg hover:bg-orange-50 flex items-center gap-2 font-semibold"
                          >
                            <Upload className="w-4 h-4" />
                            เปลี่ยนรูป
                          </label>
                          <button
                            onClick={() => updateColumn(column.id, 'imageUrl', '')}
                            className="bg-white text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 flex items-center gap-2 font-semibold"
                          >
                            <Trash2 className="w-4 h-4" />
                            ลบรูป
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label
                        htmlFor={`image-upload-${column.id}`}
                        className={`flex flex-col items-center justify-center h-48 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                          uploadingId === column.id
                            ? 'border-orange-400 bg-orange-50'
                            : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/30'
                        }`}
                      >
                        {uploadingId === column.id ? (
                          <div className="text-center">
                            <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-3" />
                            <p className="text-lg font-bold text-orange-600">{uploadProgress}%</p>
                            <p className="text-sm text-gray-600">กำลังอัปโหลด...</p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-sm font-semibold text-gray-700 mb-1">อัปโหลดรูปภาพ</p>
                            <p className="text-xs text-gray-500">PNG, JPG (แนะนำ 800x600px)</p>
                          </div>
                        )}
                      </label>
                    )}
                  </div>

                  {/* Package Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">ชื่อแพ็คเก็จ</label>
                      <div className="flex items-center gap-2">
                        <ColorSwatchPicker
                          compact
                          color={highlightColor}
                          fallback={DEFAULT_HIGHLIGHT_COLOR}
                          onChange={(hex) => updateColumn(column.id, 'highlightColor', hex)}
                        />
                        <input
                          type="text"
                          value={column.title}
                          onChange={(e) => updateColumn(column.id, 'title', e.target.value)}
                          className="flex-1 px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                          placeholder="เช่น แพ็คเก็จพื้นฐาน"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">ราคา (฿)</label>
                      <input
                        type="text"
                        value={column.price}
                        onChange={(e) => updateColumn(column.id, 'price', e.target.value)}
                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Highlighted Checkbox */}
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={column.highlighted}
                        onChange={(e) => updateColumn(column.id, 'highlighted', e.target.checked)}
                        className="w-5 h-5 text-orange-500 rounded focus:ring-orange-400"
                      />
                      <div>
                        <span className="text-sm font-bold text-gray-900 block">⭐ แพ็คเก็จแนะนำ</span>
                        <span className="text-xs text-gray-600">แสดงเป็นแพ็คเก็จเด่นบนหน้าเว็บ</span>
                      </div>
                    </label>
                  </div>

                  {/* Features */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">ฟีเจอร์ของแพ็คเก็จ</label>
                    <div className="space-y-2">
                      {column.features.map((feature, index) => (
                        <div key={index} className="flex gap-2">
                          <div className="flex-shrink-0 w-8 h-10 bg-orange-500 rounded-lg flex items-center justify-center text-white text-sm font-bold">
                            {index + 1}
                          </div>
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => updateFeature(column.id, index, e.target.value)}
                            className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                            placeholder={`ฟีเจอร์ ${index + 1}`}
                          />
                          <button
                            onClick={() => deleteFeature(column.id, index)}
                            className="flex-shrink-0 text-red-500 hover:bg-red-50 p-2 rounded-lg transition-all"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addFeature(column.id)}
                        className="w-full py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-sm font-semibold text-gray-600 hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50 transition-all flex items-center justify-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        เพิ่มฟีเจอร์ใหม่
                      </button>
                    </div>
                  </div>

                  {/* Button Settings */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">ข้อความปุ่ม</label>
                      <input
                        type="text"
                        value={column.buttonText}
                        onChange={(e) => updateColumn(column.id, 'buttonText', e.target.value)}
                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                        placeholder="เลือกแพ็คเก็จนี้"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">URL ปุ่ม</label>
                      <input
                        type="url"
                        value={column.buttonUrl}
                        onChange={(e) => updateColumn(column.id, 'buttonUrl', e.target.value)}
                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all"
                        placeholder="#"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add Package Button */}
        <button
          onClick={addColumn}
          className="w-full py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all flex items-center justify-center gap-3 group"
        >
          <div className="w-12 h-12 bg-gray-200 group-hover:bg-orange-500 rounded-full flex items-center justify-center transition-all">
            <Plus className="w-6 h-6 text-gray-600 group-hover:text-white transition-all" />
          </div>
          <span className="text-sm font-semibold text-gray-600 group-hover:text-orange-600 transition-all">เพิ่มแพ็คเก็จใหม่</span>
        </button>
      </div>
    </div>
  );
};

export default ComparisonTableBlock;
